import type { User } from './db/types';

export const NOT_AVAILABLE_GRACE_MS = 30_000;

export const RARITY = {
  COMMON: 1,
  UNCOMMON: 2,
  RARE: 3,
  EPIC: 4,
  LEGENDARY: 5,
  LOCKED: 6,
} as const;
export type RARITY_KEY = keyof typeof RARITY;
export type RARITY = typeof RARITY[keyof typeof RARITY];

export const RARITY_NAMES = new Map<RARITY, string>([
  [RARITY.COMMON, 'Common'],
  [RARITY.UNCOMMON, 'Uncommon'],
  [RARITY.RARE, 'Rare'],
  [RARITY.EPIC, 'Epic'],
  [RARITY.LEGENDARY, 'Legendary'],
  [RARITY.LOCKED, 'Locked'],
]);

export const RARITY_MULTIPLIER = new Map<RARITY, number>([
  [RARITY.COMMON, 1],
  [RARITY.UNCOMMON, 2],
  [RARITY.RARE, 3],
  [RARITY.EPIC, 4],
  [RARITY.LEGENDARY, 5],
  [RARITY.LOCKED, 6],
]);

export const RARITY_MAP = new Map<string, RARITY>([
  ['common', RARITY.COMMON],
  ['uncommon', RARITY.UNCOMMON],
  ['rare', RARITY.RARE],
  ['epic', RARITY.EPIC],
  ['legendary', RARITY.LEGENDARY],
  ['locked', RARITY.LOCKED],
]);

export const RARITY_TO_NAME = new Map<RARITY, string>([
  [RARITY.COMMON, 'common'],
  [RARITY.UNCOMMON, 'uncommon'],
  [RARITY.RARE, 'rare'],
  [RARITY.EPIC, 'epic'],
  [RARITY.LEGENDARY, 'legendary'],
  [RARITY.LOCKED, 'locked'],
]);

export const RARITY_WEIGHTS = new Map<RARITY, number>([
  [RARITY.COMMON, 0.5],
  [RARITY.UNCOMMON, 0.25],
  [RARITY.RARE, 0.15],
  [RARITY.EPIC, 0.07],
  [RARITY.LEGENDARY, 0.03],
  [RARITY.LOCKED, 0],
]);

export const RARITY_COLORS = new Map<RARITY, string>([
  [RARITY.COMMON, 'gray'],
  [RARITY.UNCOMMON, 'green'],
  [RARITY.RARE, 'blue'],
  [RARITY.EPIC, 'purple'],
  [RARITY.LEGENDARY, 'gold'],
  [RARITY.LOCKED, 'pink'],
]);

export const HUNT_DURATIONS = new Map<RARITY, [number, number]>([
  [RARITY.COMMON, [1, 2]],
  [RARITY.UNCOMMON, [1, 2]],
  [RARITY.RARE, [1, 2]],
  [RARITY.EPIC, [1, 1.5]],
  [RARITY.LEGENDARY, [0.5, 1]],
  [RARITY.LOCKED, [1, 1]],
]);

export const CAPTURE_CHANCES = new Map<RARITY, number>([
  [RARITY.COMMON, 0.4],
  [RARITY.UNCOMMON, 0.35],
  [RARITY.RARE, 0.3],
  [RARITY.EPIC, 0.25],
  [RARITY.LEGENDARY, 0.2],
  [RARITY.LOCKED, 0.1],
]);

export type Event = {
  event: string;
  id: string;
  username?: string;
  rank?: number;
  total?: number;
  user?: User;
  squishy?: SQUISHY_ID;
  type?: 'session' | 'global';
};
export const EVENTS = {
  GLOAT: 'gloat',
  CATCH: 'squishy:catch',
  APPEAR: 'squishy:appear',
  DISAPPEAR: 'squishy:disappear',
  LEADERBOARD_REFRESH: 'leaderboard:refresh',
} as const;
export type EVENT = typeof EVENTS[keyof typeof EVENTS];

export const SQUISHY_ID = {
  DEFAULT: 0,
  
  ANGEL: 1,
  DIAMOND: 2,
  EMERALD: 3,
  UNICORN: 4,
  GOLD: 5,
  SPOOKY: 6,
  BEE: 7,
  BUNNY: 8,
  LEOPARD: 9,
  SILVER: 10,
  SPARKLY: 11,
  COOKIE: 12,
  CANDY: 13,
  FAIRY: 14,
  DRAGON: 15,
  AXOLOTL: 16,
  FRANKEN: 17,
  RAINBOW: 18,
  PIKA: 19,
  AXIE: 20,
  BOO: 21,
  CHEEKY: 22,
  KODA: 23,
  LIDAL: 24,
  RAVENCLAW: 25,
  SKELETON: 26,
  SNOW_LEOPARD: 27,
  
  BEAR: 28,
  COWBOY: 29,
  DINO: 30,
  DOGGO: 31,
  DIVING: 32,
  ELEPHANT: 33,
  FROG: 34,
  FARMER: 35,
  KITTY: 36,
  SPLISH_SPLASH: 37,
  KNIGHT: 38,
  SPACE: 39,
  GENIE: 40,
  REAPER: 41,
  VAMPIRE: 42,
  SLEEPIES: 43,
  SUPER: 44,
  BA_BA_BA_BA: 45,
  BIRTHDAY: 46,
} as const;
export type SQUISHY_ID = typeof SQUISHY_ID[keyof typeof SQUISHY_ID];

export const SQUISHY_IDS = new Set<SQUISHY_ID>(
  Object.values(SQUISHY_ID),
);